import React, { useState, useEffect } from "react";
import { ContainerSelected } from "components/Environment/shared/BuildTypes";
import { Switch } from "components/Graphs/Switch";
import {
  DEFAULT_LIFECYCLE_SCRIPT,
  LifeCycleScript,
} from "components/Environment/Create/LifeCycleScript";
import { InstallJupyterOnHostToggle } from "./InstallJupyterOnHostToggle";

interface VMModeProps {
  selectedContainer: ContainerSelected;
  setSelectedContainer: (container: ContainerSelected) => void;
  localLifecycleScriptTab: "upload" | "paste";
  setLocalLifecycleScriptTab: (tab: "upload" | "paste") => void;
  localLifecycleScriptFile: File | null;
  setLocalLifecycleScriptFile: (file: File | null) => void;
  saveDescription?: string;
  onScriptValidationChange?: (isValid: boolean) => void;
  autoFocusScript?: boolean;
}

export const VMMode: React.FC<VMModeProps> = ({
  selectedContainer,
  setSelectedContainer,
  localLifecycleScriptTab,
  setLocalLifecycleScriptTab,
  localLifecycleScriptFile,
  setLocalLifecycleScriptFile,
  saveDescription = "",
  onScriptValidationChange,
  autoFocusScript,
}) => {
  const [jupyterEnabled, setJupyterEnabled] = useState<boolean>(
    selectedContainer.vmBuild?.forceJupyterInstall ?? true,
  );
  const [lifeCycleScript, setLifeCycleScript] = useState<string>(
    selectedContainer.vmBuild?.lifeCycleScriptAttr?.script || "",
  );
  const [isScriptEnabled, setIsScriptEnabled] = useState<boolean>(
    !!selectedContainer.vmBuild?.lifeCycleScriptAttr?.script,
  );

  // Update state when the container changes (e.g., when editing a launchable)
  useEffect(() => {
    setJupyterEnabled(selectedContainer.vmBuild?.forceJupyterInstall ?? true);
    const script = selectedContainer.vmBuild?.lifeCycleScriptAttr?.script || "";
    setLifeCycleScript(script);
    if (script) setIsScriptEnabled(true);
  }, [
    selectedContainer.vmBuild?.forceJupyterInstall,
    selectedContainer.vmBuild?.lifeCycleScriptAttr?.script,
  ]);

  const handleJupyterEnabledChange = (checked: boolean) => {
    setJupyterEnabled(checked);
    setSelectedContainer({
      vmBuild: {
        forceJupyterInstall: checked,
        lifeCycleScriptAttr:
          isScriptEnabled && lifeCycleScript
            ? {
                ...selectedContainer.vmBuild?.lifeCycleScriptAttr,
                script: lifeCycleScript,
              }
            : undefined,
      },
    });
  };

  const handleScriptEnabledChange = (checked: boolean) => {
    setIsScriptEnabled(checked);
    if (!checked) {
      // Clear script from container when disabled
      setSelectedContainer({
        vmBuild: {
          forceJupyterInstall: jupyterEnabled,
          lifeCycleScriptAttr: undefined,
        },
      });
    } else {
      const script = lifeCycleScript || DEFAULT_LIFECYCLE_SCRIPT;
      setLifeCycleScript(script);
      setLocalLifecycleScriptTab("paste");
      setLocalLifecycleScriptFile(null);
      setSelectedContainer({
        vmBuild: {
          forceJupyterInstall: jupyterEnabled,
          lifeCycleScriptAttr: {
            ...selectedContainer.vmBuild?.lifeCycleScriptAttr,
            script,
          },
        },
      });
    }
  };

  const handleLifeCycleScriptChange = (script: string) => {
    setLifeCycleScript(script);
    if (isScriptEnabled) {
      setSelectedContainer({
        vmBuild: {
          forceJupyterInstall: jupyterEnabled,
          lifeCycleScriptAttr: script
            ? {
                ...selectedContainer.vmBuild?.lifeCycleScriptAttr,
                script: script,
              }
            : undefined,
        },
      });
    }
  };

  return (
    <div>
      <InstallJupyterOnHostToggle
        checked={jupyterEnabled}
        onCheckedChange={handleJupyterEnabledChange}
      />
      <div className="mb-6">
        <h3 className="text-base font-medium text-white">Setup Script</h3>
        <p className="mt-1 text-sm text-secondary">
          Runs after the environment is created and the VM is ready.
        </p>
        <div className="mt-3 flex flex-row items-center text-sm text-secondary">
          <Switch
            id="script-enabled"
            checked={isScriptEnabled}
            onCheckedChange={handleScriptEnabledChange}
            className="mr-2"
          />
          <label htmlFor="script-enabled" className="text-primary">
            Run a Setup Script
          </label>
        </div>
      </div>
      {isScriptEnabled && (
        <LifeCycleScript
          lifecycleScriptTab={localLifecycleScriptTab}
          setLifecycleScriptTab={setLocalLifecycleScriptTab}
          lifecycleScriptFile={localLifecycleScriptFile}
          setLifecycleScriptFile={setLocalLifecycleScriptFile}
          script={lifeCycleScript}
          onSetScript={handleLifeCycleScriptChange}
          saveDescription={saveDescription}
          experimentalFlag={true}
          onValidationChange={onScriptValidationChange}
          autoFocus={autoFocusScript}
          hideTitle
          description=""
        />
      )}
    </div>
  );
};
